// Auto-extracted tab: dashboard
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * DashboardTab - extracted from app.jsx
 * Original lines: 5727-7408
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function DashboardTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <>
            {/* Documentation Readiness Score - V9.0 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-1">
                <DocumentationScoreWidget
                  score={0}
                  scenarios={scenarios}
                  thresholds={thresholds}
                  darkMode={darkMode}
                  onImprove={(action) => {
                    if (action === 'completeness' || action === 'citations' || action === 'evidence') {
                      setActiveTab('governance');
                    } else if (action === 'recency') {
                      setActiveTab('governance');
                    }
                  }}
                />
              </div>
              <div className="lg:col-span-2">
                {/* What's Next Widget - Personalized next actions */}
                <WhatsNextWidget
                  scenarios={scenarios}
                  thresholds={thresholds}
                  attestations={attestations}
                  darkMode={darkMode}
                  onAction={(action) => {
                    switch (action) {
                      case 'create-scenario':
                        if (editorMode === 'interview') {
                          setShowInterviewMode(true);
                        } else {
                          setInterviewModal(true);
                        }
                        break;
                      case 'create-threshold':
                        openThresholdCreate();
                        break;
                      case 'filter-low-eq':
                        setActiveTab('governance');
                        break;
                      case 'filter-overdue':
                        setActiveTab('governance');
                        break;
                      case 'examiner-package':
                        setExaminerModal({ ...examinerModal, open: true });
                        break;
                      default:
                        break;
                    }
                  }}
                />
                
                {/* 30-Day Success Path - Red Team Customer Journey Enhancement */}
                {scenarios.length < 5 && (
                  <div className="mt-4">
                    <SuccessPathWidget
                      scenarios={scenarios}
                      thresholds={thresholds}
                      attestations={attestations}
                      darkMode={darkMode}
                      onAction={(action) => {
                        switch (action) {
                          case 'create-scenario':
                            if (editorMode === 'interview') {
                              setShowInterviewMode(true);
                            } else {
                              setInterviewModal(true);
                            }
                            break;
                          case 'create-threshold':
                            openThresholdCreate();
                            break;
                          case 'improve-scenarios':
                            setActiveTab('governance');
                            break;
                          case 'examiner-package':
                            setExaminerModal({ ...examinerModal, open: true });
                            break;
                          default:
                            break;
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Exam Readiness Score - Top Priority Widget */}
            <ReadinessScoreDashboard 
              darkMode={darkMode} 
              onNavigate={(link) => {
                // Parse link and navigate
                if (link.startsWith('/governance')) setActiveTab('governance');
                else if (link.startsWith('/vendors')) setActiveTab('vendors');
                else if (link.startsWith('/regulatory')) setActiveTab('regulatory');
                else if (link.startsWith('/attestation')) setActiveTab('attestation');
              }}
            />

            {/* Executive Summary Banner - CEO/Board View */}
            {(() => {
              const complete = scenarios.filter(s => calcEQScore(s) >= 8).length + thresholds.filter(t => calcEQScore(t) >= 8).length;
              const total = scenarios.length + thresholds.length;
              const compliancePercent = total > 0 ? Math.round((complete / total) * 100) : 0;
              const lastAttestation = attestations[0];
              const criticalPending = regulatoryUpdates.filter(u => 
                (u.impact_guess === "critical" || u.impact_guess === "high") &&
                (u.review_status === "pending" || !u.review_status)
              ).length;
              const statusColor = compliancePercent >= 90 ? "emerald" : compliancePercent >= 70 ? "amber" : "red";
              const statusText = compliancePercent >= 90 ? "COMPLIANT" : compliancePercent >= 70 ? "ATTENTION NEEDED" : "ACTION REQUIRED";
              
              return (
                <div className={`mb-6 p-6 rounded-2xl border-2 shadow-xl transition-all duration-300 ${
                  darkMode ? (
                    statusColor === "emerald" ? "bg-emerald-900/30 border-emerald-500/50 shadow-emerald-900/20" :
                    statusColor === "amber" ? "bg-amber-900/30 border-amber-500/50 shadow-amber-900/20" :
                    "bg-red-900/30 border-red-500/50 shadow-red-900/20"
                  ) : (
                    statusColor === "emerald" ? "bg-emerald-50 border-emerald-300 shadow-emerald-100" :
                    statusColor === "amber" ? "bg-amber-50 border-amber-300 shadow-amber-100" :
                    "bg-red-50 border-red-300 shadow-red-100"
                  )
                }`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold shadow-lg ${
                        statusColor === "emerald" ? "bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-emerald-500/30" :
                        statusColor === "amber" ? "bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-amber-500/30" :
                        "bg-gradient-to-br from-red-500 to-red-600 text-white shadow-red-500/30"
                      }`}>
                        {compliancePercent}%
                      </div>
                      <div>
                        <div className={`text-2xl font-bold ${
                          darkMode ? (
                            statusColor === "emerald" ? "text-emerald-400" :
                            statusColor === "amber" ? "text-amber-400" :
                            "text-red-400"
                          ) : (
                            statusColor === "emerald" ? "text-emerald-800" :
                            statusColor === "amber" ? "text-amber-800" :
                            "text-red-800"
                          )
                        }`}>
                          {statusColor === "emerald" ? "✅" : statusColor === "amber" ? "⚠️" : "🔴"} {statusText}
                        </div>
                        <div className={`text-sm mt-1 ${darkMode ? "text-slate-500" : "text-slate-600"}`}>
                          {complete} of {total} governance objects meet all requirements
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
                      <div className={`rounded-xl p-3 ${darkMode ? "bg-slate-800/50 border border-slate-700/50" : "bg-white/50"}`}>
                        <div className={`text-lg font-bold ${darkMode ? "text-slate-100" : "text-slate-900"}`}>
                          {lastAttestation ? formatDate(lastAttestation.attested_at) : "Never"}
                        </div>
                        <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Last Attestation</div>
                      </div>
                      <div className={`rounded-xl p-3 ${darkMode ? "bg-slate-800/50 border border-slate-700/50" : "bg-white/50"}`}>
                        <div className={`text-lg font-bold ${criticalPending > 0 ? (darkMode ? "text-red-400" : "text-red-600") : (darkMode ? "text-emerald-400" : "text-emerald-600")}`}>
                          {criticalPending}
                        </div>
                        <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Critical Items Pending</div>
                      </div>
                      <div className={`rounded-xl p-3 ${darkMode ? "bg-slate-800/50 border border-slate-700/50" : "bg-white/50"}`}>
                        <div className={`text-lg font-bold ${darkMode ? "text-slate-100" : "text-slate-900"}`}>
                          {attestations.filter(a => new Date(a.attested_at) > new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)).length}
                        </div>
                        <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Attestations (12mo)</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            <DashboardStats scenarios={scenarios} thresholds={thresholds} attestations={attestations} darkMode={darkMode} />
            
            {/* v9.0: Recommended Scenarios for new users */}
            <RecommendedScenariosWidget 
              scenarios={scenarios}
              vertical={vertical}
              darkMode={darkMode}
              onStartInterview={(scenarioName) => {
                // Pre-populate name if provided and open Interview Mode
                setInterviewModal(true);
              }}
            />
            
            {/* v9.0: Documentation Readiness Score - THE central metric */}
            <div className="mb-6">
              <DocumentationScoreWidget 
                score={docReadinessScore} 
                loading={docScoreLoading} 
                darkMode={darkMode}
                onRefresh={loadDocReadinessScore}
              />
            </div>
            
            {/* Compliance Trend Chart - Patricia's Request */}
            {complianceHistory.length > 0 && (
              <Card darkMode={darkMode} title="Compliance Score Trend" subtitle="12-month compliance history" className="mb-6">
                <div className="h-48 flex items-end gap-1">
                  {complianceHistory.map((h, i) => {
                    const heightPercent = h.score;
                    const barColor = h.score >= 90 ? "bg-emerald-500" : h.score >= 70 ? "bg-amber-500" : "bg-red-500";
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className={`text-xs font-medium ${darkMode ? "text-slate-500" : "text-slate-500"}`}>{h.score}%</div>
                        <div 
                          className={`w-full ${barColor} rounded-t transition-all hover:opacity-80 hover:scale-105`}
                          style={{ height: `${heightPercent}%`, minHeight: "4px" }}
                          title={`${h.month}: ${h.score}% (${h.scenarios} scenarios, ${h.thresholds} thresholds)`}
                        />
                        <div className={`text-xs -rotate-45 origin-top-left mt-1 whitespace-nowrap ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                          {new Date(h.date).toLocaleDateString("en-US", { month: "short" })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className={`flex items-center justify-center gap-6 mt-6 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 bg-emerald-500 rounded" />
                    <span className={darkMode ? "text-slate-500" : "text-slate-600"}>≥90% (Compliant)</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 bg-amber-500 rounded" />
                    <span className={darkMode ? "text-slate-500" : "text-slate-600"}>70-89% (Attention)</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 bg-red-500 rounded" />
                    <span className={darkMode ? "text-slate-500" : "text-slate-600"}>&lt;70% (Action Required)</span>
                  </div>
                </div>
              </Card>
            )}
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card darkMode={darkMode} title="Monthly Attestation" helpKey="attestation" badge={attestationReadiness.canAttest ? <Badge color="emerald">Ready</Badge> : <Badge color="red">{attestationReadiness.blockingItems.length} blockers</Badge>}>
                {!attestationReadiness.canAttest ? (
                  <>
                    <div className={`rounded-xl p-4 mb-4 ${darkMode ? "bg-red-900/30 border border-red-500/30" : "bg-red-50 border border-red-200"}`}>
                      <div className={`flex items-center gap-2 font-medium text-sm ${darkMode ? "text-red-400" : "text-red-800"}`}>
                        <Icons.Alert className="w-4 h-4" />
                        {attestationReadiness.blockingItems.length} item{attestationReadiness.blockingItems.length !== 1 ? "s" : ""} blocking attestation
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {attestationReadiness.blockerSummary.map(b => (
                          <span key={b.id} className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                            {b.label}: {b.count}
                          </span>
                        ))}
                      </div>
                    </div>
                    <BlockerList blockers={blockers.slice(0, 3)} />
                    {blockers.length > 3 && <p className="text-sm text-slate-500 mt-3">...and {blockers.length - 3} more</p>}
                    <Button 
                      variant="secondary" 
                      size="sm" 
                      className="mt-3 w-full"
                      onClick={() => setActiveTab("attestations")}
                    >
                      View All Blockers
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-emerald-700 mb-4">All {scenarios.length + thresholds.length} governance objects meet the Nine Questions requirement. Ready to attest!</p>
                    <Button onClick={() => setAttestModal(true)}><Icons.Check /> Attest Now</Button>
                  </>
                )}
              </Card>
              <Card darkMode={darkMode} title="Quick Actions">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => setExaminerModal(m => ({ ...m, open: true }))}
                  >
                    <Icons.Download /> Examiner Package
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={quickAction === "boardReport"}
                    onClick={() => runQuickAction("boardReport", async () => {
                      // Generate Board Report - Professional PDF-ready HTML
                      const complete = scenarios.filter(s => calcEQScore(s) >= 8).length + thresholds.filter(t => calcEQScore(t) >= 8).length;
                      const total = scenarios.length + thresholds.length;
                      const compliancePercent = total > 0 ? Math.round((complete / total) * 100) : 0;
                      const lastAttestation = attestations[0];
                      const criticalPending = regulatoryUpdates.filter(u => 
                        (u.impact_guess === "critical" || u.impact_guess === "high") &&
                        (u.review_status === "pending" || !u.review_status)
                      ).length;
                      const attestationsLast12mo = attestations.filter(a => new Date(a.attested_at) > new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)).length;
                      const newObjectsThisQtr = scenarios.filter(s => new Date(s.created_at) > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)).length + thresholds.filter(t => new Date(t.created_at) > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)).length;
                      const statusColor = compliancePercent >= 90 ? "#10B981" : compliancePercent >= 70 ? "#F59E0B" : "#EF4444";
                      const statusText = compliancePercent >= 90 ? "COMPLIANT" : compliancePercent >= 70 ? "ATTENTION NEEDED" : "ACTION REQUIRED";
                      
                      // Build governance object summary for appendix
                      const scenarioSummary = scenarios.map(s => ({
                        name: s.name,
                        score: calcEQScore(s),
                        status: calcEQScore(s) >= 8 ? "Complete" : "Incomplete",
                        lastReview: s.last_review_date ? formatDate(s.last_review_date) : "Never",
                      }));
                      const thresholdSummary = thresholds.map(t => ({
                        name: t.name,
                        score: calcEQScore(t),
                        status: calcEQScore(t) >= 8 ? "Complete" : "Incomplete",
                        lastReview: t.last_review_date ? formatDate(t.last_review_date) : "Never",
                      }));
                      
                      // Risk breakdown
                      const highRisk = regulatoryUpdates.filter(u => u.impact_guess === "critical" || u.impact_guess === "high").length;
                      const medRisk = regulatoryUpdates.filter(u => u.impact_guess === "medium").length;
                      const lowRisk = regulatoryUpdates.filter(u => u.impact_guess === "low" || !u.impact_guess).length;
                      
                      // Previous quarter comparison - USE ACTUAL HISTORICAL DATA
                      const threeMonthsAgo = new Date();
                      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                      const prevQtrData = complianceHistory.find(h => new Date(h.date) <= threeMonthsAgo);
                      const prevQtrScore = prevQtrData?.score || compliancePercent; // Fallback to current if no history
                      const scoreChange = compliancePercent - prevQtrScore;
                      const hasHistoricalData = !!prevQtrData;
                      
                      // Attestation history for appendix
                      const recentAttestations = attestations.slice(0, 6).map(a => ({
                        date: formatDate(a.attested_at),
                        period: a.period_month,
                        attestedBy: a.attested_by_email || "Unknown",
                        status: "Completed"
                      }));
                      
                      // Generate unique report ID
                      const reportId = `RPT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
                      const generatedAt = new Date().toISOString();
                      const preparedBy = session?.user?.email || "System";
                      
                      // Get branding if available
                      const logoUrl = brandingConfig?.logoUrl || "";
                      const primaryColor = brandingConfig?.primaryColor || "#3b82f6";
                      
                      const reportHtml = `
<!DOCTYPE html>
<html>
<head>
  <title>Board Compliance Report - ${new Date().toLocaleDateString()}</title>
  <style>
    @page { 
      size: letter; 
      margin: 0.75in; 
      @bottom-center { content: "Page " counter(page) " of " counter(pages); font-size: 10px; color: #64748b; }
      @bottom-right { content: "${reportId}"; font-size: 9px; color: #94a3b8; }
    }
    @page :first { margin-top: 0; @bottom-center { content: none; } @bottom-right { content: none; } }
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #1e293b; max-width: 850px; margin: 0 auto; padding: 0; counter-reset: page; }
    
    /* Page numbers for browsers that don't support @page counters */
    .page-header { position: running(pageHeader); }
    .page-footer { 
      display: none; /* Hidden by default, shown in print */
      position: fixed; 
      bottom: 0.5in; 
      left: 0; 
      right: 0; 
      text-align: center; 
      font-size: 10px; 
      color: #64748b;
    }
    @media print { 
      .page-footer { display: block; }
      .page-footer::after { content: " • ${reportId}"; color: #94a3b8; }
    }
    
    /* Confidential watermark (optional) */
    .watermark { 
      position: fixed; 
      top: 50%; 
      left: 50%; 
      transform: translate(-50%, -50%) rotate(-45deg); 
      font-size: 100px; 
      color: rgba(0,0,0,0.03); 
      font-weight: bold; 
      pointer-events: none; 
      z-index: -1;
      display: none;
    }
    .show-watermark .watermark { display: block; }
    
    /* Cover Page */
    .cover-page { 
      min-height: 100vh; 
      display: flex; 
      flex-direction: column; 
      justify-content: center; 
      align-items: center; 
      text-align: center;
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
      color: white;
      padding: 60px;
      page-break-after: always;
    }
    .cover-logo { 
      font-size: 48px; 
      font-weight: bold; 
      margin-bottom: 16px;
      background: linear-gradient(135deg, #60a5fa, #a78bfa);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    .cover-org-logo { max-width: 200px; max-height: 80px; margin-bottom: 40px; }
    .cover-title { font-size: 36px; font-weight: 700; margin-bottom: 8px; }
    .cover-subtitle { font-size: 18px; color: #94a3b8; margin-bottom: 40px; }
    .cover-org { font-size: 24px; font-weight: 600; margin-bottom: 8px; }
    .cover-date { font-size: 16px; color: #94a3b8; }
    .cover-badge { 
      display: inline-block; 
      padding: 12px 32px; 
      border-radius: 12px; 
      font-size: 18px; 
      font-weight: 600;
      margin-top: 40px;
      background: ${statusColor}; 
      color: white;
    }
    .cover-score { font-size: 64px; font-weight: bold; margin-top: 20px; }
    
    /* Main Content */
    .main-content { padding: 40px; }
    .header { border-bottom: 3px solid ${primaryColor}; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: center; }
    .header-left { }
    .header-logo { font-size: 24px; font-weight: bold; color: #1e293b; }
    .header-org-logo { max-height: 40px; }
    .subtitle { color: #64748b; font-size: 14px; margin-top: 5px; }
    
    /* Executive Summary */
    .exec-summary { background: #f8fafc; border-radius: 12px; padding: 24px; margin-bottom: 30px; border-left: 4px solid ${primaryColor}; }
    .exec-summary-title { font-size: 14px; font-weight: 600; color: ${primaryColor}; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
    .exec-summary-text { font-size: 15px; line-height: 1.7; }
    
    .status-banner { background: ${statusColor}15; border: 2px solid ${statusColor}; border-radius: 12px; padding: 24px; margin-bottom: 30px; text-align: center; }
    .status-score { font-size: 48px; font-weight: bold; color: ${statusColor}; }
    .status-label { font-size: 18px; font-weight: 600; color: ${statusColor}; margin-top: 8px; }
    .status-detail { color: #64748b; margin-top: 8px; }
    .status-change { font-size: 14px; margin-top: 12px; padding: 8px 16px; border-radius: 20px; display: inline-block; background: ${scoreChange >= 0 ? '#dcfce7' : '#fef2f2'}; color: ${scoreChange >= 0 ? '#166534' : '#991b1b'}; }
    
    .metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 30px; }
    .metric { background: #f8fafc; border-radius: 8px; padding: 16px; text-align: center; border: 1px solid #e2e8f0; }
    .metric-value { font-size: 24px; font-weight: bold; color: #1e293b; }
    .metric-label { font-size: 11px; color: #64748b; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.3px; }
    
    .section { margin-bottom: 24px; }
    .section-title { font-size: 16px; font-weight: 600; color: #1e293b; border-bottom: 2px solid ${primaryColor}; padding-bottom: 8px; margin-bottom: 12px; }
    .alert { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; }
    .alert-critical { background: #fef2f2; border-left: 4px solid #ef4444; }
    .alert-success { background: #f0fdf4; border-left: 4px solid #10b981; }
    .recommendation { background: #f8fafc; border-radius: 8px; padding: 16px; }
    
    /* Risk Breakdown */
    .risk-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px; }
    .risk-item { padding: 16px; border-radius: 8px; text-align: center; }
    .risk-high { background: #fef2f2; border: 1px solid #fecaca; }
    .risk-med { background: #fefce8; border: 1px solid #fef08a; }
    .risk-low { background: #f0fdf4; border: 1px solid #bbf7d0; }
    .risk-count { font-size: 28px; font-weight: bold; }
    .risk-label { font-size: 12px; color: #64748b; }
    
    /* Trend Chart (simple bar representation) */
    .trend-chart { display: flex; align-items: flex-end; gap: 8px; height: 100px; margin: 20px 0; padding: 0 20px; }
    .trend-bar { flex: 1; background: ${primaryColor}; border-radius: 4px 4px 0 0; transition: height 0.3s; }
    .trend-labels { display: flex; justify-content: space-between; font-size: 10px; color: #64748b; padding: 0 20px; }
    
    /* Appendix Table - LARGER FONT for readability */
    .appendix { page-break-before: always; padding: 40px; }
    .appendix-title { font-size: 22px; font-weight: bold; margin-bottom: 24px; color: #1e293b; border-bottom: 3px solid ${primaryColor}; padding-bottom: 10px; }
    .appendix-subtitle { font-size: 16px; font-weight: 600; color: #475569; margin: 24px 0 12px 0; }
    .appendix-section { margin-bottom: 32px; }
    .report-id-header { text-align: right; font-size: 11px; color: #94a3b8; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 24px; font-size: 14px; } /* Increased from 12px */
    th { background: #f1f5f9; padding: 12px 10px; text-align: left; font-weight: 600; border-bottom: 2px solid #e2e8f0; font-size: 13px; }
    td { padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 14px; } /* Increased from 8px padding */
    tr:hover { background: #f8fafc; }
    .status-complete { color: #059669; font-weight: 600; }
    .status-incomplete { color: #dc2626; font-weight: 600; }
    .score-cell { font-weight: 600; }
    .risk-badge { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; text-transform: uppercase; }
    .risk-high { background: #fef2f2; color: #991b1b; }
    .risk-medium { background: #fefce8; color: #854d0e; }
    .risk-low { background: #f0fdf4; color: #166534; }
    
    /* Table of Contents */
    .toc { margin-bottom: 40px; page-break-after: always; }
    .toc-title { font-size: 20px; font-weight: bold; margin-bottom: 20px; border-bottom: 2px solid ${primaryColor}; padding-bottom: 8px; }
    .toc-list { list-style: none; padding: 0; }
    .toc-item { padding: 8px 0; border-bottom: 1px dotted #e2e8f0; }
    .toc-item a { color: ${primaryColor}; text-decoration: none; }
    .toc-item a:hover { text-decoration: underline; }
    
    /* Signature Section */
    .signature-section { margin-top: 60px; padding-top: 30px; border-top: 2px solid #e2e8f0; }
    .signature-title { font-size: 14px; font-weight: 600; color: #64748b; margin-bottom: 20px; text-transform: uppercase; letter-spacing: 0.5px; }
    .prepared-by { background: #f8fafc; border-radius: 8px; padding: 16px; margin-bottom: 24px; }
    .prepared-by-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px; }
    .prepared-by-value { font-size: 14px; font-weight: 500; color: #1e293b; margin-top: 4px; }
    .signature-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; }
    .signature-box { }
    .signature-line { border-bottom: 1px solid #1e293b; height: 40px; margin-bottom: 8px; }
    .signature-label { font-size: 13px; color: #64748b; font-weight: 500; }
    .signature-date { margin-top: 20px; }
    .date-field { display: flex; align-items: center; gap: 8px; margin-top: 16px; }
    .date-label { font-size: 13px; color: #64748b; }
    .date-line { flex: 1; max-width: 200px; border-bottom: 1px solid #1e293b; height: 24px; }
    
    /* Next Actions */
    .actions-list { list-style: none; padding: 0; margin: 0; }
    .actions-list li { padding: 12px 16px; background: #f8fafc; border-radius: 8px; margin-bottom: 8px; display: flex; align-items: center; gap: 12px; }
    .action-priority { font-size: 10px; font-weight: 600; padding: 4px 8px; border-radius: 4px; text-transform: uppercase; }
    .priority-high { background: #fef2f2; color: #991b1b; }
    .priority-med { background: #fefce8; color: #854d0e; }
    
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; color: #94a3b8; font-size: 11px; }
    .print-buttons { position: fixed; top: 20px; right: 20px; display: flex; gap: 8px; z-index: 100; }
    .print-button { background: #3b82f6; color: white; border: none; padding: 12px 24px; border-radius: 8px; font-size: 14px; cursor: pointer; }
    .print-button:hover { background: #2563eb; }
    .print-button.secondary { background: #64748b; }
    @media print { .print-buttons { display: none; } .cover-page { min-height: auto; page-break-after: always; } }
  </style>
</head>
<body>
  <div class="print-buttons">
    <button class="print-button" onclick="window.print()">📄 Print / Save as PDF</button>
    <button class="print-button secondary" onclick="document.querySelector('.cover-page').style.display='none'">📝 Skip Cover Page</button>
    <button class="print-button secondary" onclick="document.querySelector('.cover-page').style.background='#f8fafc'; document.querySelector('.cover-page').style.color='#1e293b';">🖨️ Printer-Friendly Cover</button>
  </div>
  
  <!-- COVER PAGE -->
  <div class="cover-page">
    ${logoUrl ? `<img src="${logoUrl}" class="cover-org-logo" alt="Organization Logo" />` : ''}
    <div class="cover-logo">STEADFASTLY</div>
    <div class="cover-title">Board Compliance Report</div>
    <div class="cover-subtitle">${VERTICAL_CONFIG[vertical]?.displayName || vertical} Governance Program</div>
    <div class="cover-org">${org?.name || "Organization"}</div>
    <div class="cover-date">${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
    <div class="cover-score">${compliancePercent}%</div>
    <div class="cover-badge">${statusText}</div>
  </div>
  
  <!-- EXECUTIVE HIGHLIGHT (1-page summary for CEO) -->
  <div class="exec-highlight" style="page-break-after: always; padding: 40px;">
    <div style="text-align: center; margin-bottom: 30px;">
      <div style="font-size: 20px; font-weight: 700; color: #1e293b;">EXECUTIVE HIGHLIGHT</div>
      <div style="font-size: 12px; color: #64748b; margin-top: 4px;">One-Page Compliance Summary • ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
    </div>
    
    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; margin-bottom: 24px;">
      <div style="background: ${compliancePercent >= 90 ? '#f0fdf4' : compliancePercent >= 70 ? '#fefce8' : '#fef2f2'}; border: 2px solid ${compliancePercent >= 90 ? '#10b981' : compliancePercent >= 70 ? '#eab308' : '#ef4444'}; border-radius: 12px; padding: 20px; text-align: center;">
        <div style="font-size: 36px; font-weight: 800; color: ${compliancePercent >= 90 ? '#059669' : compliancePercent >= 70 ? '#ca8a04' : '#dc2626'};">${compliancePercent}%</div>
        <div style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">Compliance Score</div>
      </div>
      <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; text-align: center;">
        <div style="font-size: 36px; font-weight: 800; color: #1e293b;">${complete}/${total}</div>
        <div style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">Objects Complete</div>
      </div>
      <div style="background: ${criticalPending > 0 ? '#fef2f2' : '#f0fdf4'}; border: 2px solid ${criticalPending > 0 ? '#ef4444' : '#10b981'}; border-radius: 12px; padding: 20px; text-align: center;">
        <div style="font-size: 36px; font-weight: 800; color: ${criticalPending > 0 ? '#dc2626' : '#059669'};">${criticalPending}</div>
        <div style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">Critical Pending</div>
      </div>
    </div>
    
    <div style="background: #f8fafc; border-radius: 8px; padding: 16px; margin-bottom: 20px;">
      <div style="font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 8px;">📊 BOTTOM LINE</div>
      <div style="font-size: 14px; line-height: 1.6; color: #1e293b;">
        ${compliancePercent >= 90 
          ? `<strong style="color: #059669;">COMPLIANT.</strong> The compliance program is operating effectively at ${compliancePercent}%. No board action required. Continue current practices.`
          : compliancePercent >= 70
          ? `<strong style="color: #ca8a04;">ATTENTION NEEDED.</strong> Compliance at ${compliancePercent}% with ${criticalPending} critical item(s) pending. Management has a 30-day remediation plan.`
          : `<strong style="color: #dc2626;">ACTION REQUIRED.</strong> Compliance at ${compliancePercent}% requires immediate attention. ${criticalPending} critical items pending. Recommend weekly board updates until resolved.`}
      </div>
    </div>
    
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px;">
      <div style="background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px;">
        <div style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; margin-bottom: 8px;">Key Metrics</div>
        <div style="font-size: 13px; color: #1e293b;">
          <div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #f1f5f9;">
            <span>Last Attestation</span>
            <strong>${lastAttestation ? formatDate(lastAttestation.attested_at) : "Never"}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #f1f5f9;">
            <span>Attestations (12mo)</span>
            <strong>${attestationsLast12mo}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 4px 0;">
            <span>Trend vs Prior Quarter</span>
            <strong style="color: ${scoreChange >= 0 ? '#059669' : '#dc2626'};">${hasHistoricalData ? (scoreChange >= 0 ? '↑' : '↓') + ' ' + Math.abs(scoreChange) + '%' : 'N/A'}</strong>
          </div>
        </div>
      </div>
      <div style="background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px;">
        <div style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; margin-bottom: 8px;">Risk Breakdown</div>
        <div style="font-size: 13px; color: #1e293b;">
          <div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #f1f5f9;">
            <span style="color: #dc2626;">● Critical/High</span>
            <strong>${highRisk}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #f1f5f9;">
            <span style="color: #ca8a04;">● Medium</span>
            <strong>${medRisk}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 4px 0;">
            <span style="color: #059669;">● Low</span>
            <strong>${lowRisk}</strong>
          </div>
        </div>
      </div>
    </div>
    
    ${criticalPending > 0 || incomplete > 0 ? `
    <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 16px;">
      <div style="font-size: 11px; font-weight: 600; color: #991b1b; text-transform: uppercase; margin-bottom: 8px;">⚠️ Requires Board Attention</div>
      <ul style="margin: 0; padding-left: 20px; font-size: 13px; color: #1e293b;">
        ${criticalPending > 0 ? `<li><strong>${criticalPending}</strong> critical regulatory update(s) await review</li>` : ''}
        ${incomplete > 0 ? `<li><strong>${incomplete}</strong> governance object(s) are incomplete</li>` : ''}
      </ul>
    </div>
    ` : `
    <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 16px;">
      <div style="font-size: 11px; font-weight: 600; color: #166534; text-transform: uppercase; margin-bottom: 8px;">✅ No Items Requiring Board Attention</div>
      <div style="font-size: 13px; color: #1e293b;">All governance objects are complete and all critical regulatory updates have been reviewed.</div>
    </div>
    `}
    
    <div style="margin-top: 20px; text-align: center; font-size: 11px; color: #94a3b8;">
      See full report for detailed analysis • Report ID: ${reportId}
    </div>
  </div>
  
  <!-- MAIN REPORT -->
  <div class="main-content">
    <div class="header">
      <div class="header-left">
        ${logoUrl ? `<img src="${logoUrl}" class="header-org-logo" alt="" />` : `<div class="header-logo">${org?.name || "STEADFASTLY"}</div>`}
        <div class="subtitle">Board Compliance Report • ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
        <div class="subtitle">${VERTICAL_CONFIG[vertical]?.displayName || vertical} • ${JURISDICTION_CONFIG[jurisdiction]?.name || jurisdiction}</div>
      </div>
    </div>
    
    <!-- Executive Summary -->
    <div class="exec-summary" id="sec-executive-summary">
      <div class="exec-summary-title">📋 Executive Summary</div>
      <div class="exec-summary-text">
        ${compliancePercent >= 90 
          ? `The ${VERTICAL_CONFIG[vertical]?.displayName || vertical} compliance program is operating at <strong>${compliancePercent}%</strong> effectiveness. All critical regulatory requirements are being met, and the governance framework is functioning as designed. The Board may proceed with confidence in the organization's compliance posture.`
          : compliancePercent >= 70
          ? `The ${VERTICAL_CONFIG[vertical]?.displayName || vertical} compliance program is at <strong>${compliancePercent}%</strong> effectiveness, indicating areas requiring management attention. ${criticalPending} critical regulatory item(s) await review. Management has identified remediation priorities and expects to address gaps within the next review cycle.`
          : `The ${VERTICAL_CONFIG[vertical]?.displayName || vertical} compliance program requires <strong>immediate attention</strong> at ${compliancePercent}% effectiveness. ${criticalPending} critical items are pending, and ${incomplete} governance objects are incomplete. Management recommends a focused remediation effort with weekly progress reporting.`}
      </div>
    </div>
    
    <div class="status-banner" id="sec-compliance-status">
      <div class="status-score">${compliancePercent}%</div>
      <div class="status-label">${statusText}</div>
      <div class="status-detail">${complete} of ${total} governance objects meet all requirements</div>
      ${hasHistoricalData 
        ? `<div class="status-change">${scoreChange >= 0 ? '↑' : '↓'} ${Math.abs(scoreChange)}% vs. Previous Quarter (${prevQtrData?.month || 'Q-1'})</div>`
        : `<div class="status-change" style="background: #f1f5f9; color: #64748b;">First reporting period - no prior quarter data</div>`
      }
    </div>
    
    <div class="metrics">
      <div class="metric">
        <div class="metric-value">${lastAttestation ? formatDate(lastAttestation.attested_at) : "Never"}</div>
        <div class="metric-label">Last Attestation</div>
      </div>
      <div class="metric">
        <div class="metric-value" style="color: ${criticalPending > 0 ? '#ef4444' : '#10b981'}">${criticalPending}</div>
        <div class="metric-label">Critical Pending</div>
      </div>
      <div class="metric">
        <div class="metric-value">${attestationsLast12mo}</div>
        <div class="metric-label">Attestations (12mo)</div>
      </div>
      <div class="metric">
        <div class="metric-value">${newObjectsThisQtr}</div>
        <div class="metric-label">New This Quarter</div>
      </div>
    </div>
    
    <div class="section" id="sec-risk-breakdown">
      <div class="section-title">📊 Risk Rating Breakdown</div>
      <div class="risk-grid">
        <div class="risk-item risk-high">
          <div class="risk-count" style="color: #dc2626">${highRisk}</div>
          <div class="risk-label">CRITICAL/HIGH</div>
        </div>
        <div class="risk-item risk-med">
          <div class="risk-count" style="color: #ca8a04">${medRisk}</div>
          <div class="risk-label">MEDIUM</div>
        </div>
        <div class="risk-item risk-low">
          <div class="risk-count" style="color: #059669">${lowRisk}</div>
          <div class="risk-label">LOW/INFO</div>
        </div>
      </div>
    </div>
    
    <div class="section" id="sec-regulatory-status">
      <div class="section-title">📋 Regulatory Status</div>
      ${criticalPending > 0 
        ? `<div class="alert alert-critical"><strong>⚠️ Action Required:</strong> ${criticalPending} CRITICAL/HIGH regulatory update(s) require review before the next attestation.</div>`
        : `<div class="alert alert-success"><strong>✅ All Clear:</strong> All critical regulatory updates have been reviewed.</div>`
      }
    </div>
    
    <div class="section" id="sec-next-actions">
      <div class="section-title">🎯 Recommended Next Actions</div>
      <ul class="actions-list">
        ${criticalPending > 0 ? `<li><span class="action-priority priority-high">High</span> Review ${criticalPending} pending critical regulatory updates</li>` : ''}
        ${incomplete > 0 ? `<li><span class="action-priority priority-high">High</span> Complete Nine Questions for ${incomplete} governance object(s)</li>` : ''}
        ${!lastAttestation || new Date(lastAttestation.attested_at) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) ? `<li><span class="action-priority priority-med">Medium</span> Submit monthly attestation</li>` : ''}
        <li><span class="action-priority priority-med">Medium</span> Schedule next board compliance review</li>
      </ul>
    </div>
    
    <div class="section" id="sec-management-rec">
      <div class="section-title">💡 Management Recommendation</div>
      <div class="recommendation">
        ${compliancePercent >= 90 
          ? "The compliance program is operating effectively. Continue current monitoring practices and maintain the strong governance framework. No immediate actions required beyond routine maintenance."
          : compliancePercent >= 70
          ? "The compliance program has some gaps that require attention. Management should address incomplete governance objects and pending regulatory reviews before the next board meeting. A 30-day remediation plan is recommended."
          : "The compliance program requires immediate attention. Management should prioritize remediation of blocking items and schedule a follow-up review within 30 days. Consider engaging additional resources to accelerate remediation."}
      </div>
    </div>
    
    <!-- Signature Section -->
    <div class="signature-section" id="sec-attestation-approval">
      <div class="signature-title">ATTESTATION & APPROVAL</div>
      
      <!-- Prepared By -->
      <div class="prepared-by">
        <div class="prepared-by-label">Report Prepared By</div>
        <div class="prepared-by-value">${preparedBy}</div>
        <div style="margin-top: 8px; font-size: 12px; color: #64748b;">Generated: ${new Date().toLocaleString()}</div>
      </div>
      
      <div class="signature-grid">
        <div class="signature-box">
          <div class="signature-line"></div>
          <div class="signature-label">${VERTICAL_CONFIG[vertical]?.officerTitle || "Compliance Officer"}</div>
          <div class="date-field">
            <span class="date-label">Date:</span>
            <div class="date-line"></div>
          </div>
        </div>
        <div class="signature-box">
          <div class="signature-line"></div>
          <div class="signature-label">Board Chair / Committee Chair</div>
          <div class="date-field">
            <span class="date-label">Date:</span>
            <div class="date-line"></div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="footer">
      <p>Generated by Steadfastly • Report ID: ${reportId}</p>
      <p>${generatedAt} • ${VERTICAL_CONFIG[vertical]?.displayName} • ${JURISDICTION_CONFIG[jurisdiction]?.name}</p>
    </div>
  </div>
  
  <!-- TABLE OF CONTENTS -->
  <div class="toc">
    <div class="report-id-header">Report ID: ${reportId}</div>
    <div class="toc-title">Table of Contents</div>
    <ul class="toc-list">
      <li class="toc-item"><a href="#sec-executive-summary">Executive Summary</a></li>
      <li class="toc-item"><a href="#sec-compliance-status">Compliance Status Overview</a></li>
      <li class="toc-item"><a href="#sec-risk-breakdown">Risk Rating Breakdown</a></li>
      <li class="toc-item"><a href="#sec-regulatory-status">Regulatory Status</a></li>
      <li class="toc-item"><a href="#sec-next-actions">Recommended Next Actions</a></li>
      <li class="toc-item"><a href="#sec-management-rec">Management Recommendation</a></li>
      <li class="toc-item"><a href="#sec-attestation-approval">Attestation & Approval</a></li>
      <li class="toc-item"><a href="#sec-appendix-a">Appendix A: Governance Objects</a></li>
      <li class="toc-item"><a href="#sec-appendix-b">Appendix B: Attestation History</a></li>
    </ul>
    
    <div style="margin-top: 40px; padding: 20px; background: #f8fafc; border-radius: 8px;">
      <div style="font-size: 12px; color: #64748b; margin-bottom: 8px;">REPORT METADATA</div>
      <table style="font-size: 13px; margin: 0;">
        <tr><td style="width: 140px; color: #64748b; border: none; padding: 4px 0;">Report ID:</td><td style="border: none; padding: 4px 0; font-weight: 500;">${reportId}</td></tr>
        <tr><td style="color: #64748b; border: none; padding: 4px 0;">Generated:</td><td style="border: none; padding: 4px 0;">${new Date().toLocaleString()}</td></tr>
        <tr><td style="color: #64748b; border: none; padding: 4px 0;">Prepared By:</td><td style="border: none; padding: 4px 0;">${preparedBy}</td></tr>
        <tr><td style="color: #64748b; border: none; padding: 4px 0;">Organization:</td><td style="border: none; padding: 4px 0;">${org?.name || "Unknown"}</td></tr>
        <tr><td style="color: #64748b; border: none; padding: 4px 0;">Vertical:</td><td style="border: none; padding: 4px 0;">${VERTICAL_CONFIG[vertical]?.displayName || vertical}</td></tr>
        <tr><td style="color: #64748b; border: none; padding: 4px 0;">Jurisdiction:</td><td style="border: none; padding: 4px 0;">${JURISDICTION_CONFIG[jurisdiction]?.name || jurisdiction}</td></tr>
      </table>
    </div>
  </div>
  
  <!-- APPENDIX A -->
  <div class="appendix" id="sec-appendix-a">
    <div class="report-id-header">Report ID: ${reportId}</div>
    <div class="appendix-title">Appendix A: Governance Object Summary</div>
    
    <div class="appendix-section">
      <div class="appendix-subtitle">Scenarios (${scenarios.length})</div>
      <table>
        <thead>
          <tr>
            <th style="width: 40%">Scenario Name</th>
            <th style="width: 12%">EQ Score</th>
            <th style="width: 12%">Status</th>
            <th style="width: 16%">Risk Rating</th>
            <th style="width: 20%">Last Review</th>
          </tr>
        </thead>
        <tbody>
          ${scenarios.map(s => {
            const score = calcEQScore(s);
            const riskRating = s.risk_rating || "low";
            return `
              <tr>
                <td><strong>${s.name}</strong></td>
                <td class="score-cell">${score}/8</td>
                <td class="${score >= 8 ? 'status-complete' : 'status-incomplete'}">${score >= 8 ? 'Complete' : 'Incomplete'}</td>
                <td><span class="risk-badge risk-${riskRating}">${riskRating}</span></td>
                <td>${s.last_review_date ? formatDate(s.last_review_date) : 'Never'}</td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
    
    <div class="appendix-section">
      <div class="appendix-subtitle">Thresholds (${thresholds.length})</div>
      <table>
        <thead>
          <tr>
            <th style="width: 40%">Threshold Name</th>
            <th style="width: 12%">EQ Score</th>
            <th style="width: 12%">Status</th>
            <th style="width: 16%">Current Value</th>
            <th style="width: 20%">Last Review</th>
          </tr>
        </thead>
        <tbody>
          ${thresholds.map(t => {
            const score = calcEQScore(t);
            return `
              <tr>
                <td><strong>${t.name}</strong></td>
                <td class="score-cell">${score}/8</td>
                <td class="${score >= 8 ? 'status-complete' : 'status-incomplete'}">${score >= 8 ? 'Complete' : 'Incomplete'}</td>
                <td>${t.current_value || '—'}</td>
                <td>${t.last_review_date ? formatDate(t.last_review_date) : 'Never'}</td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
  </div>
  
  <!-- APPENDIX B -->
  <div class="appendix" id="sec-appendix-b">
    <div class="report-id-header">Report ID: ${reportId}</div>
    <div class="appendix-title">Appendix B: Attestation History</div>
    
    <div class="appendix-section">
      <div class="appendix-subtitle">Recent Attestations (Last 6)</div>
      ${recentAttestations.length > 0 ? `
        <table>
          <thead>
            <tr>
              <th style="width: 25%">Date</th>
              <th style="width: 25%">Period</th>
              <th style="width: 30%">Attested By</th>
              <th style="width: 20%">Status</th>
            </tr>
          </thead>
          <tbody>
            ${recentAttestations.map(a => `
              <tr>
                <td>${a.date}</td>
                <td>${a.period}</td>
                <td>${a.attestedBy}</td>
                <td class="status-complete">${a.status}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : `
        <p style="color: #64748b; font-style: italic;">No attestation history available.</p>
      `}
    </div>
    
    <div class="appendix-section">
      <div class="appendix-subtitle">Compliance Trend (12 Months)</div>
      ${complianceHistory.length > 0 ? `
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th>Score</th>
              <th>Scenarios</th>
              <th>Thresholds</th>
              <th>Trend</th>
            </tr>
          </thead>
          <tbody>
            ${complianceHistory.slice(0, 12).map((h, i) => {
              const prevScore = complianceHistory[i + 1]?.score || h.score;
              const diff = h.score - prevScore;
              return `
                <tr>
                  <td>${h.month}</td>
                  <td class="score-cell">${h.score}%</td>
                  <td>${h.scenarios || 0}</td>
                  <td>${h.thresholds || 0}</td>
                  <td style="color: ${diff >= 0 ? '#059669' : '#dc2626'};">${diff >= 0 ? '↑' : '↓'} ${Math.abs(diff)}%</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      ` : `
        <p style="color: #64748b; font-style: italic;">No historical compliance data available yet.</p>
      `}
    </div>
    
    <div class="footer" style="margin-top: 60px;">
      <p><strong>CONFIDENTIAL</strong> - For Board and Management Use Only</p>
      <p>This report contains sensitive compliance information. Do not distribute outside the organization.</p>
      <p style="margin-top: 12px; font-size: 10px;">Report ID: ${reportId} • Generated: ${generatedAt}</p>
    </div>
  </div>
  
  <div class="watermark">CONFIDENTIAL</div>
</body>
</html>`;
                      
                      // Open in new window for printing
                      const printWindow = window.open("", "_blank", "width=900,height=700");
                      printWindow.document.write(reportHtml);
                      printWindow.document.close();
                      setToast({ type: "success", message: "Board report opened - click Print to save as PDF" });
                      setTimeout(() => setToast(null), 3000);
                    })}
                  >
                    <Icons.Download /> {quickAction === "boardReport" ? "Generating..." : "Board Report"}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={quickAction === "analystReport"}
                    onClick={() => runQuickAction("analystReport", async () => {
                      // Generate Analyst Detail Report - Granular compliance data
                      const complete = scenarios.filter(s => calcEQScore(s) >= 8).length + thresholds.filter(t => calcEQScore(t) >= 8).length;
                      const total = scenarios.length + thresholds.length;
                      const compliancePercent = total > 0 ? Math.round((complete / total) * 100) : 0;
                      
                      // Evidence inventory
                      const evidenceByType = {};
                      documents.forEach(doc => {
                        const type = doc.file_type || "unknown";
                        evidenceByType[type] = (evidenceByType[type] || 0) + 1;
                      });
                      
                      // Regulatory summary by source
                      const regBySource = {};
                      const regByImpact = { critical: 0, high: 0, medium: 0, low: 0 };
                      regulatoryUpdates.forEach(u => {
                        const source = u.source || "Unknown";
                        regBySource[source] = (regBySource[source] || 0) + 1;
                        regByImpact[u.impact_guess || "low"]++;
                      });
                      
                      // Gap analysis with evidence file names and remediation guidance
                      // Uses the correct Nine Questions from the spec
                      const EQ_REMEDIATION = {
                        WHO: "Assign a named individual owner who is accountable for defending this decision to examiners",
                        WHAT: "Document the specific policy, threshold, or scenario with precise configuration details", 
                        WHEN: "Record when this was established and when it takes effect (system timestamps)",
                        WHY: "Provide substantive rationale explaining why this is the right value and what alternatives were considered (min 50 chars, no placeholder text)",
                        WHERE: "Specify the exact URL or record ID where supporting documentation is located",
                        AUTHORITY: "Cite the specific regulatory authority with section numbers (e.g., 31 CFR 1010.311)",
                        EVIDENCE: "Attach documents proving this is implemented, not just documented",
                        REVIEW: "Set a specific next review date and trigger conditions"
                      };
                      
                      const gaps = [];
                      scenarios.forEach(s => {
                        const score = calcEQScore(s);
                        if (score < 8) {
                          // Use getMissingFields which returns correct labels: WHO, WHAT, WHEN, WHY, WHERE, AUTHORITY, EVIDENCE, REVIEW
                          const missing = getMissingFields(s);
                          const remediation = missing.map(label => EQ_REMEDIATION[label]).filter(Boolean);
                          // Get actual evidence file names
                          const evidenceFiles = s.evidence_files || [];
                          gaps.push({ 
                            type: "Scenario", 
                            name: s.name, 
                            score, 
                            missing, 
                            remediation,
                            evidenceCount: evidenceFiles.length,
                            evidenceNames: evidenceFiles.slice(0, 3).map(f => f.name || f.filename || "Unknown").join(", "),
                            owner: s.owner_email || s.owner || "Unassigned"
                          });
                        }
                      });
                      thresholds.forEach(t => {
                        const score = calcEQScore(t);
                        if (score < 8) {
                          // Use getMissingFields which returns correct labels: WHO, WHAT, WHEN, WHY, WHERE, AUTHORITY, EVIDENCE, REVIEW
                          const missing = getMissingFields(t);
                          const remediation = missing.map(label => EQ_REMEDIATION[label]).filter(Boolean);
                          const evidenceFiles = t.evidence_files || [];
                          gaps.push({ 
                            type: "Threshold", 
                            name: t.name, 
                            score, 
                            missing,
                            remediation,
                            evidenceCount: evidenceFiles.length,
                            evidenceNames: evidenceFiles.slice(0, 3).map(f => f.name || f.filename || "Unknown").join(", "),
                            owner: t.owner_email || t.owner || "Unassigned"
                          });
                        }
                      });
                      
                      // Get critical regulatory updates with titles and SLA countdown
                      const SLA_DAYS = { critical: 7, high: 14, medium: 30, low: 90 };
                      const criticalRegUpdates = regulatoryUpdates
                        .filter(u => u.impact_guess === "critical" || u.impact_guess === "high")
                        .filter(u => u.review_status !== "reviewed")
                        .slice(0, 10)
                        .map(u => {
                          const publishedDate = u.published_at ? new Date(u.published_at) : new Date();
                          const slaDays = SLA_DAYS[u.impact_guess] || 14;
                          const dueDate = new Date(publishedDate.getTime() + slaDays * 24 * 60 * 60 * 1000);
                          const daysRemaining = Math.ceil((dueDate - new Date()) / (24 * 60 * 60 * 1000));
                          return {
                            title: u.title || "Untitled",
                            source: u.source || "Unknown",
                            impact: u.impact_guess || "unknown",
                            published: u.published_at ? formatDate(u.published_at) : "Unknown",
                            link: u.link || "",
                            sla: daysRemaining,
                            slaStatus: daysRemaining < 0 ? "overdue" : daysRemaining <= 3 ? "urgent" : "ok"
                          };
                        });
                      
                      const primaryColor = brandingConfig?.primaryColor || "#3b82f6";
                      
                      const analystHtml = `
<!DOCTYPE html>
<html>
<head>
  <title>Analyst Detail Report - ${new Date().toLocaleDateString()}</title>
  <style>
    @page { size: letter; margin: 0.75in; }
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.5; color: #1e293b; max-width: 900px; margin: 0 auto; padding: 30px; font-size: 12px; }
    .header { border-bottom: 2px solid ${primaryColor}; padding-bottom: 16px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: flex-end; }
    .header-title { font-size: 20px; font-weight: bold; color: #1e293b; }
    .header-meta { text-align: right; color: #64748b; font-size: 11px; }
    h2 { font-size: 14px; font-weight: 600; color: #1e293b; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin: 24px 0 12px 0; }
    h3 { font-size: 12px; font-weight: 600; color: #475569; margin: 16px 0 8px 0; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 11px; }
    th { background: #f1f5f9; padding: 8px 6px; text-align: left; font-weight: 600; border-bottom: 2px solid #e2e8f0; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; }
    td { padding: 6px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
    tr:hover { background: #f8fafc; }
    .status-complete { color: #059669; font-weight: 600; }
    .status-incomplete { color: #dc2626; font-weight: 600; }
    .score-8 { color: #059669; }
    .score-low { color: #dc2626; }
    .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 500; }
    .badge-critical { background: #fef2f2; color: #991b1b; }
    .badge-high { background: #fff7ed; color: #c2410c; }
    .badge-medium { background: #fefce8; color: #854d0e; }
    .badge-low { background: #f0fdf4; color: #166534; }
    .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
    .summary-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; text-align: center; }
    .summary-value { font-size: 24px; font-weight: bold; color: #1e293b; }
    .summary-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.3px; }
    .gap-item { background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; padding: 10px; margin-bottom: 8px; }
    .gap-title { font-weight: 600; color: #991b1b; }
    .gap-detail { font-size: 11px; color: #64748b; margin-top: 4px; }
    .evidence-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; }
    .evidence-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px; text-align: center; }
    .evidence-count { font-size: 18px; font-weight: bold; color: #1e293b; }
    .evidence-type { font-size: 10px; color: #64748b; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; text-align: center; color: #94a3b8; font-size: 10px; }
    .print-button { position: fixed; top: 20px; right: 20px; background: #3b82f6; color: white; border: none; padding: 10px 20px; border-radius: 6px; font-size: 12px; cursor: pointer; }
    @media print { .print-button { display: none; } }
    .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
  </style>
</head>
<body>
  <button class="print-button" onclick="window.print()">📄 Print / Save as PDF</button>
  
  <div class="header">
    <div>
      <div class="header-title">📊 Analyst Detail Report</div>
      <div style="color: #64748b; margin-top: 4px;">${org?.name || "Organization"} • ${VERTICAL_CONFIG[vertical]?.displayName || vertical}</div>
    </div>
    <div class="header-meta">
      <div>Generated: ${new Date().toLocaleString()}</div>
      <div>Jurisdiction: ${JURISDICTION_CONFIG[jurisdiction]?.name || jurisdiction}</div>
    </div>
  </div>
  
  <div class="summary-grid">
    <div class="summary-card">
      <div class="summary-value">${compliancePercent}%</div>
      <div class="summary-label">Compliance Score</div>
    </div>
    <div class="summary-card">
      <div class="summary-value">${complete}/${total}</div>
      <div class="summary-label">Objects Complete</div>
    </div>
    <div class="summary-card">
      <div class="summary-value">${gaps.length}</div>
      <div class="summary-label">Items with Gaps</div>
    </div>
    <div class="summary-card">
      <div class="summary-value">${documents.length}</div>
      <div class="summary-label">Evidence Documents</div>
    </div>
  </div>
  
  <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 20px;">
    <div style="font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; margin-bottom: 10px;">📈 Trend Indicators</div>
    <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; font-size: 12px;">
      <div>
        <span style="color: #64748b;">vs Last Month:</span>
        <strong style="color: ${compliancePercent >= 85 ? '#059669' : '#dc2626'};">${compliancePercent >= 85 ? '↑' : '↓'} Stable</strong>
      </div>
      <div>
        <span style="color: #64748b;">Overdue Reviews:</span>
        <strong style="color: ${scenarios.filter(s => s.next_review_date && new Date(s.next_review_date) < new Date()).length > 0 ? '#dc2626' : '#059669'};">
          ${scenarios.filter(s => s.next_review_date && new Date(s.next_review_date) < new Date()).length + thresholds.filter(t => t.next_review_date && new Date(t.next_review_date) < new Date()).length}
        </strong>
      </div>
      <div>
        <span style="color: #64748b;">SLA Breaches:</span>
        <strong style="color: ${criticalRegUpdates.filter(u => u.slaStatus === 'overdue').length > 0 ? '#dc2626' : '#059669'};">
          ${criticalRegUpdates.filter(u => u.slaStatus === 'overdue').length}
        </strong>
      </div>
      <div>
        <span style="color: #64748b;">Attestation Status:</span>
        <strong style="color: ${attestations.length > 0 ? '#059669' : '#d97706'};">
          ${attestations.length > 0 ? 'Current' : 'Pending'}
        </strong>
      </div>
    </div>
  </div>
  
  <h2>📁 Gap Analysis (${gaps.length} items need attention)</h2>
  ${gaps.length > 0 ? gaps.map(g => `
    <div class="gap-item">
      <div class="gap-title">${g.type}: ${g.name}</div>
      <div class="gap-detail">
        <strong>Score:</strong> ${g.score}/8 • <strong>Owner:</strong> ${g.owner} • <strong>Evidence:</strong> ${g.evidenceCount} files
        ${g.evidenceNames ? `<br/><strong>Files:</strong> ${g.evidenceNames}${g.evidenceCount > 3 ? ` (+${g.evidenceCount - 3} more)` : ''}` : ''}
      </div>
      <div style="margin-top: 8px; padding-top: 8px; border-top: 1px dashed #fecaca;">
        <strong style="color: #991b1b;">Missing Questions:</strong> ${g.missing.join(", ") || "None"}
        <div style="margin-top: 4px; font-size: 10px; color: #64748b;">
          <strong>Remediation Steps:</strong>
          <ul style="margin: 4px 0 0 16px; padding: 0;">
            ${g.remediation.slice(0, 3).map(r => `<li>${r}</li>`).join('')}
            ${g.remediation.length > 3 ? `<li><em>...and ${g.remediation.length - 3} more actions</em></li>` : ''}
          </ul>
        </div>
      </div>
    </div>
  `).join('') : '<p style="color: #059669;">✅ No gaps found - all governance objects are complete!</p>'}
  
  ${criticalRegUpdates.length > 0 ? `
  <h2>🚨 Critical Regulatory Updates Pending Review (${criticalRegUpdates.length})</h2>
  <table>
    <thead>
      <tr>
        <th style="width: 40%">Title</th>
        <th>Source</th>
        <th>Impact</th>
        <th>Published</th>
        <th>SLA Status</th>
      </tr>
    </thead>
    <tbody>
      ${criticalRegUpdates.map(u => `
        <tr>
          <td>${u.link ? `<a href="${u.link}" target="_blank" style="color: #3b82f6;">${u.title}</a>` : u.title}</td>
          <td>${u.source}</td>
          <td><span class="badge badge-${u.impact}">${u.impact.toUpperCase()}</span></td>
          <td>${u.published}</td>
          <td style="font-weight: 600; color: ${u.slaStatus === 'overdue' ? '#dc2626' : u.slaStatus === 'urgent' ? '#d97706' : '#059669'};">
            ${u.slaStatus === 'overdue' ? `⚠️ OVERDUE (${Math.abs(u.sla)}d)` : u.slaStatus === 'urgent' ? `⏰ ${u.sla}d left` : `✓ ${u.sla}d left`}
          </td>
        </tr>
      `).join('')}
    </tbody>
  </table>
  ` : ''}
  
  <div class="two-col">
    <div>
      <h2>📑 Evidence Inventory (${documents.length} files)</h2>
      <div class="evidence-grid">
        ${Object.entries(evidenceByType).map(([type, count]) => `
          <div class="evidence-item">
            <div class="evidence-count">${count}</div>
            <div class="evidence-type">${type.toUpperCase()}</div>
          </div>
        `).join('')}
      </div>
    </div>
    <div>
      <h2>📰 Regulatory by Impact</h2>
      <div class="evidence-grid" style="grid-template-columns: repeat(4, 1fr);">
        <div class="evidence-item" style="background: #fef2f2; border-color: #fecaca;">
          <div class="evidence-count" style="color: #991b1b;">${regByImpact.critical}</div>
          <div class="evidence-type">Critical</div>
        </div>
        <div class="evidence-item" style="background: #fff7ed; border-color: #fed7aa;">
          <div class="evidence-count" style="color: #c2410c;">${regByImpact.high}</div>
          <div class="evidence-type">High</div>
        </div>
        <div class="evidence-item" style="background: #fefce8; border-color: #fef08a;">
          <div class="evidence-count" style="color: #854d0e;">${regByImpact.medium}</div>
          <div class="evidence-type">Medium</div>
        </div>
        <div class="evidence-item" style="background: #f0fdf4; border-color: #bbf7d0;">
          <div class="evidence-count" style="color: #166534;">${regByImpact.low}</div>
          <div class="evidence-type">Low</div>
        </div>
      </div>
    </div>
  </div>
  
  <h2>🔍 Scenarios Detail (${scenarios.length})</h2>
  <table>
    <thead>
      <tr>
        <th style="width: 25%">Name</th>
        <th>Score</th>
        <th>Status</th>
        <th>Owner</th>
        <th>Last Test</th>
        <th>Next Review</th>
        <th>Evidence</th>
        <th>Risk</th>
      </tr>
    </thead>
    <tbody>
      ${scenarios.map(s => {
        const score = calcEQScore(s);
        const nextReview = s.next_review_date ? new Date(s.next_review_date) : null;
        const isOverdue = nextReview && nextReview < new Date();
        return `
          <tr>
            <td><strong>${s.name}</strong></td>
            <td class="${score >= 8 ? 'score-8' : 'score-low'}">${score}/8</td>
            <td class="${score >= 8 ? 'status-complete' : 'status-incomplete'}">${score >= 8 ? '✓' : '✗'}</td>
            <td>${s.owner || '—'}</td>
            <td>${s.last_review_date ? formatDate(s.last_review_date) : '—'}</td>
            <td style="color: ${isOverdue ? '#dc2626' : '#1e293b'};">${nextReview ? formatDate(s.next_review_date) : '—'}${isOverdue ? ' ⚠️' : ''}</td>
            <td>${s.evidence_files?.length || 0}</td>
            <td><span class="badge badge-${s.risk_rating || 'low'}">${(s.risk_rating || 'low').toUpperCase()}</span></td>
          </tr>
        `;
      }).join('')}
    </tbody>
  </table>
  
  <h2>📏 Thresholds Detail (${thresholds.length})</h2>
  <table>
    <thead>
      <tr>
        <th style="width: 25%">Name</th>
        <th>Score</th>
        <th>Status</th>
        <th>Value</th>
        <th>Last Test</th>
        <th>Next Review</th>
        <th>Evidence</th>
      </tr>
    </thead>
    <tbody>
      ${thresholds.map(t => {
        const score = calcEQScore(t);
        const nextReview = t.next_review_date ? new Date(t.next_review_date) : null;
        const isOverdue = nextReview && nextReview < new Date();
        return `
          <tr>
            <td><strong>${t.name}</strong></td>
            <td class="${score >= 8 ? 'score-8' : 'score-low'}">${score}/8</td>
            <td class="${score >= 8 ? 'status-complete' : 'status-incomplete'}">${score >= 8 ? '✓' : '✗'}</td>
            <td>${t.current_value || '—'}</td>
            <td>${t.last_review_date ? formatDate(t.last_review_date) : '—'}</td>
            <td style="color: ${isOverdue ? '#dc2626' : '#1e293b'};">${nextReview ? formatDate(t.next_review_date) : '—'}${isOverdue ? ' ⚠️' : ''}</td>
            <td>${t.evidence_files?.length || 0}</td>
          </tr>
        `;
      }).join('')}
    </tbody>
  </table>
  
  <h2>📰 Regulatory Updates by Source</h2>
  <table>
    <thead>
      <tr>
        <th>Source</th>
        <th>Count</th>
        <th>% of Total</th>
      </tr>
    </thead>
    <tbody>
      ${Object.entries(regBySource).sort((a, b) => b[1] - a[1]).map(([source, count]) => `
        <tr>
          <td>${source}</td>
          <td>${count}</td>
          <td>${regulatoryUpdates.length > 0 ? Math.round((count / regulatoryUpdates.length) * 100) : 0}%</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
  
  <div class="footer">
    <p>Analyst Detail Report • Generated by Steadfastly</p>
    <p>${new Date().toISOString()} • For compliance analysis purposes</p>
  </div>
</body>
</html>`;
                      
                      const printWindow = window.open("", "_blank", "width=950,height=700");
                      printWindow.document.write(analystHtml);
                      printWindow.document.close();
                      setToast({ type: "success", message: "Analyst report opened - click Print to save as PDF" });
                      setTimeout(() => setToast(null), 3000);
                    })}
                  >
                    <Icons.Download /> {quickAction === "analystReport" ? "Generating..." : "Analyst Report"}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setShowReportBuilder(true)}
                  >
                    <Icons.FileText /> Report Builder
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={quickAction === "export"}
                    onClick={() => runQuickAction("export", async () => {
                      const out = await callFn("export-data", { organization_id: orgId, vertical, format: "json", entities: { scenarios: true, thresholds: true, attestations: true } });
                      const blob = new Blob([JSON.stringify(out, null, 2)], { type: "application/json" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `steadfastly-${vertical}.json`;
                      a.click();
                    })}
                  >
                    <Icons.Download /> {quickAction === "export" ? "Exporting..." : "Export Data"}
                  </Button>
                  <Button variant="secondary" onClick={() => { setActiveTab("audit"); verifyAuditChain(); }}><Icons.Link /> Verify Chain</Button>
                  <Button variant="secondary" disabled={dataLoading} onClick={() => loadAllData()}><Icons.Check /> {dataLoading ? "Refreshing..." : "Refresh"}</Button>
                </div>
              </Card>
            </div>
            
            {/* Enhanced Analytics Widgets - v9.0 */}
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              {/* Vendor Risk Summary Widget */}
              <Card darkMode={darkMode} title="Third-Party Risk" helpKey="thirdPartyRisk" actions={<Button variant="ghost" size="xs" onClick={() => setActiveTab("vendor-risk")}>View All →</Button>}>
                {(() => {
                  const criticalVendors = vendors.filter(v => v.risk_tier === "critical");
                  const highRiskVendors = vendors.filter(v => v.inherent_risk === "high" || v.inherent_risk === "critical");
                  const pendingAssessments = vendorAssessments.filter(a => a.status === "sent" || a.status === "in_progress");
                  const overdueAssessments = vendorAssessments.filter(a => a.status === "sent" && new Date(a.due_date) < new Date());
                  
                  return (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Total Vendors</span>
                        <span className={`font-bold text-lg ${darkMode ? "text-slate-100" : "text-slate-900"}`}>{vendors.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Critical Tier</span>
                        <span className="font-bold text-lg text-red-500">{criticalVendors.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>High Risk</span>
                        <span className="font-bold text-lg text-amber-500">{highRiskVendors.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Pending Assessments</span>
                        <span className={`font-bold ${pendingAssessments.length > 0 ? "text-blue-500" : "text-emerald-500"}`}>{pendingAssessments.length}</span>
                      </div>
                      {overdueAssessments.length > 0 && (
                        <div className="mt-2 p-2 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">
                          ⚠️ {overdueAssessments.length} overdue assessment{overdueAssessments.length > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Card>

              {/* Controls Health Widget */}
              <Card darkMode={darkMode} title="Controls Health" helpKey="controls" actions={<Button variant="ghost" size="xs" onClick={() => setActiveTab("controls")}>View All →</Button>}>
                {(() => {
                  const effectiveControls = controls.filter(c => c.effectiveness === "effective");
                  const ineffectiveControls = controls.filter(c => c.effectiveness === "ineffective");
                  const recentTests = controlTests.filter(t => {
                    const testDate = new Date(t.tested_at);
                    const thirtyDaysAgo = new Date();
                    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                    return testDate >= thirtyDaysAgo;
                  });
                  const failedTests = controlTests.filter(t => t.result === "fail");
                  const openIssues = controlIssues.filter(i => i.status !== "closed" && i.status !== "resolved");
                  
                  return (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Total Controls</span>
                        <span className={`font-bold text-lg ${darkMode ? "text-slate-100" : "text-slate-900"}`}>{controls.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Effective</span>
                        <span className="font-bold text-lg text-emerald-500">{effectiveControls.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Ineffective</span>
                        <span className={`font-bold text-lg ${ineffectiveControls.length > 0 ? "text-red-500" : "text-emerald-500"}`}>{ineffectiveControls.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Tests (30d)</span>
                        <span className="font-bold text-blue-500">{recentTests.length}</span>
                      </div>
                      {openIssues.length > 0 && (
                        <div className="mt-2 p-2 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm">
                          🔧 {openIssues.length} open control issue{openIssues.length > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Card>

              {/* Incidents & Risk Widget */}
              <Card darkMode={darkMode} title="Incidents & Risk" helpKey="incidents" actions={<Button variant="ghost" size="xs" onClick={() => setActiveTab("incidents")}>View All →</Button>}>
                {(() => {
                  const openIncidents = incidents.filter(i => i.status !== "closed" && i.status !== "resolved");
                  const criticalIncidents = openIncidents.filter(i => i.severity === "critical");
                  const highRisks = risks.filter(r => r.inherent_risk_score >= 16 || r.inherent_likelihood >= 4);
                  const thisMonthIncidents = incidents.filter(i => {
                    const date = new Date(i.detected_at);
                    const now = new Date();
                    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                  });
                  
                  return (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Open Incidents</span>
                        <span className={`font-bold text-lg ${openIncidents.length > 0 ? "text-amber-500" : "text-emerald-500"}`}>{openIncidents.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>Critical</span>
                        <span className={`font-bold text-lg ${criticalIncidents.length > 0 ? "text-red-500" : "text-emerald-500"}`}>{criticalIncidents.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>This Month</span>
                        <span className={`font-bold ${darkMode ? "text-slate-100" : "text-slate-900"}`}>{thisMonthIncidents.length}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-600"}`}>High Risks</span>
                        <span className={`font-bold ${highRisks.length > 0 ? "text-red-500" : "text-emerald-500"}`}>{highRisks.length}</span>
                      </div>
                      {criticalIncidents.length > 0 && (
                        <div className="mt-2 p-2 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">
                          🚨 {criticalIncidents.length} critical incident{criticalIncidents.length > 1 ? "s" : ""} requiring attention
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Card>
            </div>

            {/* Risk Heat Map - Modern 2026 Design */}
            <div className="mt-6 grid lg:grid-cols-2 gap-6">
              {/* Compact Heat Map */}
              <Card darkMode={darkMode} title="Risk Matrix" actions={<Button variant="ghost" size="xs" onClick={() => setActiveTab("risks")}>View All →</Button>}>
                <div className="flex gap-4">
                  {/* Y-axis label */}
                  <div className="flex flex-col justify-center">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${darkMode ? "text-slate-500" : "text-slate-500"} transform -rotate-90 whitespace-nowrap`}>
                      Likelihood
                    </span>
                  </div>
                  
                  {/* Heat Map Grid */}
                  <div className="flex-1">
                    {/* Column labels */}
                    <div className="grid grid-cols-6 gap-0.5 mb-1">
                      <div></div>
                      {["Negligible", "Minor", "Moderate", "Major", "Severe"].map((label, i) => (
                        <div key={i} className={`text-[9px] text-center font-medium ${darkMode ? "text-slate-500" : "text-slate-500"} truncate px-0.5`}>
                          {label}
                        </div>
                      ))}
                    </div>
                    
                    {/* Matrix rows */}
                    {[
                      { level: 5, label: "Certain" },
                      { level: 4, label: "Likely" },
                      { level: 3, label: "Possible" },
                      { level: 2, label: "Unlikely" },
                      { level: 1, label: "Rare" }
                    ].map(({ level, label }) => (
                      <div key={level} className="grid grid-cols-6 gap-0.5 mb-0.5">
                        <div className={`text-[9px] flex items-center justify-end pr-1 font-medium ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                          {label}
                        </div>
                        {[1, 2, 3, 4, 5].map(impact => {
                          const score = level * impact;
                          const count = risks.filter(r => r.inherent_likelihood === level && r.inherent_impact === impact).length;
                          
                          // Vivid, modern color gradient
                          const cellStyle = score >= 20 
                            ? "bg-gradient-to-br from-red-500 to-red-600 shadow-red-500/30" 
                            : score >= 15 
                              ? "bg-gradient-to-br from-orange-500 to-red-500 shadow-orange-500/30"
                              : score >= 10 
                                ? "bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/30"
                                : score >= 5 
                                  ? "bg-gradient-to-br from-yellow-400 to-amber-400 shadow-yellow-500/30"
                                  : "bg-gradient-to-br from-emerald-400 to-green-500 shadow-green-500/30";
                          
                          return (
                            <div 
                              key={`${level}-${impact}`}
                              className={`aspect-square rounded-md ${cellStyle} shadow-sm flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110 hover:shadow-lg hover:z-10`}
                              title={`${label} likelihood × ${["Negligible", "Minor", "Moderate", "Major", "Severe"][impact-1]} impact = ${score}`}
                            >
                              {count > 0 && (
                                <span className="text-white text-xs font-bold drop-shadow-sm">{count}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    
                    {/* X-axis label */}
                    <div className={`text-[10px] font-semibold uppercase tracking-wider text-center mt-2 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                      Impact
                    </div>
                  </div>
                </div>
                
                {/* Legend - horizontal, compact */}
                <div className="flex items-center justify-center gap-3 mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-emerald-400 to-green-500"></span>
                    <span className={`text-[10px] ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Low</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-yellow-400 to-amber-400"></span>
                    <span className={`text-[10px] ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Medium</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-amber-400 to-orange-500"></span>
                    <span className={`text-[10px] ${darkMode ? "text-slate-500" : "text-slate-500"}`}>High</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-orange-500 to-red-500"></span>
                    <span className={`text-[10px] ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Very High</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-red-500 to-red-600"></span>
                    <span className={`text-[10px] ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Critical</span>
                  </span>
                </div>
              </Card>
              
              {/* Risk Summary Cards - Modern Stats */}
              <div className="grid grid-cols-2 gap-3">
                {/* Critical Risks */}
                <div className={`rounded-xl p-4 ${darkMode ? "bg-gradient-to-br from-red-900/40 to-red-950/40 border border-red-800/30" : "bg-gradient-to-br from-red-50 to-red-100/80 border border-red-200"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-red-400" : "text-red-600"}`}>Critical</span>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-500/20">
                      <span className="text-white text-sm font-bold">{risks.filter(r => (r.inherent_likelihood * r.inherent_impact) >= 20).length}</span>
                    </div>
                  </div>
                  <div className={`text-[11px] mt-2 ${darkMode ? "text-red-300/70" : "text-red-700/70"}`}>Score 20-25</div>
                </div>
                
                {/* High Risks */}
                <div className={`rounded-xl p-4 ${darkMode ? "bg-gradient-to-br from-orange-900/40 to-orange-950/40 border border-orange-800/30" : "bg-gradient-to-br from-orange-50 to-orange-100/80 border border-orange-200"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-orange-400" : "text-orange-600"}`}>High</span>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
                      <span className="text-white text-sm font-bold">{risks.filter(r => { const s = r.inherent_likelihood * r.inherent_impact; return s >= 15 && s < 20; }).length}</span>
                    </div>
                  </div>
                  <div className={`text-[11px] mt-2 ${darkMode ? "text-orange-300/70" : "text-orange-700/70"}`}>Score 15-19</div>
                </div>
                
                {/* Medium Risks */}
                <div className={`rounded-xl p-4 ${darkMode ? "bg-gradient-to-br from-amber-900/40 to-amber-950/40 border border-amber-800/30" : "bg-gradient-to-br from-amber-50 to-amber-100/80 border border-amber-200"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-amber-400" : "text-amber-600"}`}>Medium</span>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                      <span className="text-white text-sm font-bold">{risks.filter(r => { const s = r.inherent_likelihood * r.inherent_impact; return s >= 5 && s < 15; }).length}</span>
                    </div>
                  </div>
                  <div className={`text-[11px] mt-2 ${darkMode ? "text-amber-300/70" : "text-amber-700/70"}`}>Score 5-14</div>
                </div>
                
                {/* Low Risks */}
                <div className={`rounded-xl p-4 ${darkMode ? "bg-gradient-to-br from-emerald-900/40 to-emerald-950/40 border border-emerald-800/30" : "bg-gradient-to-br from-emerald-50 to-emerald-100/80 border border-emerald-200"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-emerald-400" : "text-emerald-600"}`}>Low</span>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                      <span className="text-white text-sm font-bold">{risks.filter(r => (r.inherent_likelihood * r.inherent_impact) < 5).length}</span>
                    </div>
                  </div>
                  <div className={`text-[11px] mt-2 ${darkMode ? "text-emerald-300/70" : "text-emerald-700/70"}`}>Score 1-4</div>
                </div>
              </div>
            </div>
            
            {/* Executive Summary & ROI - Red Team Customer Journey Enhancement */}
            <div className="mt-6">
              <ExecutiveSummaryWidget 
                scenarios={scenarios} 
                thresholds={thresholds} 
                attestations={attestations} 
                darkMode={darkMode} 
              />
            </div>

            {/* Training & Progress Sidebar */}
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <LearningProgressWidget scenarios={scenarios} thresholds={thresholds} darkMode={darkMode} />
              <ActivityFeedWidget darkMode={darkMode} />
            </div>
            
            {/* Nine Questions Explainer - Learn the framework */}
            <div className={`rounded-2xl p-5 ${darkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'} shadow-sm`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Learn the Nine Questions Framework</h4>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                    Every governance decision must answer nine key questions. Master this framework to pass any examination.
                  </p>
                </div>
                <Button variant="secondary" onClick={() => setShowNineQuestionsExplainer(true)}>
                  <Icons.HelpCircle className="w-4 h-4 mr-1.5" />
                  View Framework
                </Button>
              </div>
            </div>
          </>
    </>
  );
}

export default DashboardTab;
